/* ============================================
   CustomSelect — Premium Dropdown
   ============================================ */

.cselect {
  position: relative;
  width: 100%;
}

.cselect-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.cselect-label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

/* ---- Trigger ---- */

.cselect-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 14px;
  background: var(--input-bg, #f8fafc);
  border: 1.5px solid var(--border-color, #cbd5e1);
  border-radius: var(--border-radius-md, 10px);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: var(--font-family);
  font-size: var(--font-size-sm, 13.5px);
  color: var(--text-primary, #0f172a);
  outline: none;
  min-height: 40px;
}

.cselect-trigger:hover {
  border-color: var(--wb-primary, #cb11ab) !important;
  box-shadow: 0 0 0 4px rgba(203, 17, 171, 0.12), 0 4px 12px rgba(203, 17, 171, 0.08);
  background: var(--surface-color, #ffffff);
  transform: translateY(-1px);
}

.cselect-trigger-open {
  border-color: var(--wb-primary, #cb11ab) !important;
  box-shadow: 0 0 0 4px rgba(203, 17, 171, 0.18), 0 4px 16px rgba(203, 17, 171, 0.12);
  background: var(--surface-color, #ffffff);
}

.cselect-value {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cselect-value-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.cselect-placeholder {
  color: var(--text-tertiary);
}

.cselect-chevron {
  display: flex;
  align-items: center;
  color: var(--text-tertiary);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  margin-left: 8px;
}

.cselect-chevron-open {
  transform: rotate(180deg);
  color: var(--wb-primary);
}

/* ---- Dropdown (rendered via Portal) ---- */

.cselect-portal {
  pointer-events: auto;
}

.cselect-dropdown {
  background: var(--surface);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  animation: cselectDropIn 0.18s ease-out;
  overflow: hidden;
}

[data-theme="dark"] .cselect-dropdown {
  background: rgba(15, 23, 42, 0.98);
  border-color: rgba(59, 130, 246, 0.15);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.08);
}

@keyframes cselectDropIn {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ---- Search ---- */

.cselect-search-wrap {
  padding: 8px 8px 4px;
}

.cselect-search {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius-sm);
  background: var(--bg-secondary);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.cselect-search:focus {
  border-color: var(--wb-primary);
}

.cselect-search::placeholder {
  color: var(--text-tertiary);
}

/* ---- Options List ---- */

.cselect-options {
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
}

.cselect-options::-webkit-scrollbar {
  width: 4px;
}

.cselect-options::-webkit-scrollbar-track {
  background: transparent;
}

.cselect-options::-webkit-scrollbar-thumb {
  background: var(--surface-border);
  border-radius: 2px;
}

/* ---- Option ---- */

.cselect-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: var(--border-radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  transition: all 0.12s ease;
  text-align: left;
  position: relative;
}

.cselect-option:hover {
  background: var(--bg-secondary);
}

[data-theme="dark"] .cselect-option:hover {
  background: rgba(59, 130, 246, 0.06);
}

.cselect-option-active {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(147, 51, 234, 0.06));
  font-weight: 600;
}

.cselect-option-active:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(147, 51, 234, 0.1));
}

[data-theme="dark"] .cselect-option-active {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(147, 51, 234, 0.1));
}

.cselect-option-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
}

.cselect-option-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cselect-option-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(99, 102, 241, 0.1);
  color: var(--wb-primary);
  flex-shrink: 0;
}

.cselect-check {
  color: var(--wb-primary);
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* ---- Group Label ---- */

.cselect-group-label {
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-tertiary);
}

/* ---- Empty ---- */

.cselect-empty {
  padding: 20px;
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
}

/* ---- Mobile ---- */
@media (max-width: 768px) {
  .cselect-trigger {
    min-height: 44px;
    font-size: 16px; /* iOS zoom prevention */
    padding: 10px 14px;
  }

  .cselect-option {
    padding: 12px 14px;
    min-height: 44px;
    font-size: 15px;
  }

  .cselect-search {
    font-size: 16px !important;
    padding: 10px 14px;
    min-height: 44px;
  }

  .cselect-options {
    max-height: 50vh;
  }

  .cselect-dropdown {
    border-radius: 14px;
  }
}
